import React from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';

// CONCEPT 1 — Swiss Spectral Precision Editorial
const PreviewConcept1 = () => {
  return (
    <main className="pt-20 pb-24 min-h-screen" style={{ backgroundColor: '#FAFAFA' }} data-testid="preview-c1">
      {/* Concept banner */}
      <div className="fixed top-20 right-6 z-40 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-lg">
        <span className="text-[10px] uppercase tracking-widest font-mono text-[#FF4E50] font-bold">Preview · Concept 1</span>
      </div>

      {/* HERO — Swiss Grid */}
      <section className="max-w-[1280px] mx-auto px-8 pt-16">
        {/* Top monospace ISO badge */}
        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.22em] text-[#0A2A63] pb-14 border-b border-slate-200">
          <span className="h-1.5 w-1.5 rounded-full bg-[#FF4E50]" />
          ISO 12647-2 · G7 MASTER CERTIFIED · SINCE 1998
        </div>

        <div className="grid grid-cols-12 gap-8 pt-20">
          <div className="col-span-12 lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-6">01 — Enterprise color infrastructure</p>
            <h1 className="text-6xl lg:text-7xl font-black tracking-[-0.03em] leading-[0.95] text-[#0A2A63]">
              Precision color<br/>management<br/>
              <span className="text-slate-400">infrastructure for</span><br/>
              industrial print.
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-lg leading-relaxed">
              Eliminate waste. Guarantee <span className="font-mono text-[#0A2A63] font-bold">ΔE &lt; 1.0</span> across every press,
              every shift, every substrate. Trusted by 400+ enterprise printers in North America.
            </p>

            {/* Precision stats row */}
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md border-t border-slate-200 pt-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Avg ΔE2000</p>
                <p className="text-3xl font-black text-[#0A2A63] mt-1">0.42</p>
              </div>
              <div className="border-l border-slate-200 pl-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Ink savings</p>
                <p className="text-3xl font-black text-[#0A2A63] mt-1">-38%</p>
              </div>
              <div className="border-l border-slate-200 pl-6">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Installs</p>
                <p className="text-3xl font-black text-[#0A2A63] mt-1">400+</p>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button className="group bg-[#0A2A63] text-white rounded-none h-12 px-8 font-bold text-sm uppercase tracking-widest hover:bg-black transition">
                Request Enterprise Demo <ArrowRight className="inline h-4 w-4 ml-2 group-hover:translate-x-1 transition" />
              </button>
              <button className="rounded-none h-12 px-8 font-bold text-sm uppercase tracking-widest border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white transition">
                Explore Hardware
              </button>
            </div>
          </div>

          {/* Right — Product card */}
          <div className="col-span-12 lg:col-span-5">
            <div className="bg-white border border-slate-200 rounded-none p-6 relative">
              <div className="absolute -top-3 left-6 bg-[#FAFAFA] px-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-[#FF4E50] font-bold">Featured instrument</p>
              </div>

              <img src="/barbieri/frame_045.webp" alt="Barbieri Spectro LFP qb" className="w-full h-64 object-contain" />

              <div className="mt-4 flex items-baseline justify-between">
                <div>
                  <h3 className="text-xl font-black text-[#0A2A63]">Barbieri Spectro LFP qb</h3>
                  <p className="text-xs text-slate-500 font-mono uppercase tracking-widest mt-1">Textile Edition · Motorized XY</p>
                </div>
                <span className="font-mono text-xs bg-[#0A2A63] text-white px-2 py-1">M0-M3</span>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-[10px] text-slate-600 border-t border-slate-200 pt-4">
                <div>
                  <p className="uppercase tracking-widest text-slate-400">Aperture</p>
                  <p className="text-slate-900 font-bold mt-1">2 · 4 · 6 · 8 mm</p>
                </div>
                <div>
                  <p className="uppercase tracking-widest text-slate-400">Speed</p>
                  <p className="text-slate-900 font-bold mt-1">1,650 patches/min</p>
                </div>
                <div>
                  <p className="uppercase tracking-widest text-slate-400">ΔE00 IIA</p>
                  <p className="text-slate-900 font-bold mt-1">&lt; 0.4</p>
                </div>
              </div>

              <button className="mt-6 w-full bg-transparent text-[#0A2A63] font-bold text-xs uppercase tracking-widest border-t border-slate-200 pt-4 flex items-center justify-between hover:text-[#FF4E50] transition">
                Inspect spectral curve <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="max-w-[1280px] mx-auto px-8 mt-24 pt-10 border-t border-slate-200">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-6">Trusted color partners</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 items-center">
          {['barbieri','nix','printfactory','impoproof'].map(b => (
            <div key={b} className="h-16 flex items-center justify-center border border-slate-200 bg-white hover:border-[#0A2A63] transition">
              <img src={`/logos/${b}.png`} alt={b} className="max-h-8 object-contain grayscale hover:grayscale-0 transition" />
            </div>
          ))}
          <div className="h-16 flex items-center justify-center border border-slate-200 bg-white font-mono text-[10px] uppercase tracking-widest text-slate-600 text-center px-2">
            ISO 12647-2<br/>Certified
          </div>
        </div>
      </section>

      {/* Section 2 — Capability rows */}
      <section className="max-w-[1280px] mx-auto px-8 mt-32 grid grid-cols-12 gap-8">
        <div className="col-span-12 lg:col-span-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#FF4E50] mb-4">02 — Capability</p>
          <h2 className="text-4xl lg:text-5xl font-black tracking-tight text-[#0A2A63] leading-[1.05]">
            One measurement<br/>standard.<br/>Every substrate.
          </h2>
          <p className="mt-6 text-slate-600 max-w-md">
            From wide-format solvent to ceramic tile — Barbieri&apos;s motorized head reads them all with reference-grade repeatability.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-8 divide-y divide-slate-200 border-y border-slate-200">
          {[
            ['01', 'Wide-format & signage', 'Solvent · Latex · UV · Aqueous', 'ISO 12647-8'],
            ['02', 'Textile & décor', 'Direct-to-fabric · Non-woven · Wallpaper', 'ISO 12647-9'],
            ['03', 'Packaging & labels', 'Spot color libraries · Brand tolerances', 'ISO 12647-6'],
            ['04', 'Ceramic & tile', 'Gloss & matte polarization', 'Custom'],
          ].map(([n, title, subs, iso]) => (
            <div key={n} className="grid grid-cols-12 gap-4 py-6 items-center group hover:bg-white transition">
              <span className="col-span-1 font-mono text-xs text-slate-400">{n}</span>
              <div className="col-span-5">
                <p className="font-black text-[#0A2A63] text-lg group-hover:text-[#FF4E50] transition">{title}</p>
                <p className="text-xs text-slate-500 mt-1">{subs}</p>
              </div>
              <span className="col-span-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">{iso}</span>
              <span className="col-span-3 text-right">
                <ChevronDown className="inline h-4 w-4 -rotate-90 text-slate-400 group-hover:text-[#FF4E50] group-hover:translate-x-1 transition" />
              </span>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default PreviewConcept1;
